/**
 * OrbitalMechanicsPassiveSkillEffect — Astral dense-path passive:
 * bonus damage per tile of Astral-sourced enemy displacement.
 *
 * When this passive unlocks, it registers a listener for displacement
 * events. Any enemy displaced by an Astral-tree skill (Slingshot knockback,
 * Warp Geometry pulse, Meteo impact, Collapse, etc.) takes
 * `tilesTraveled * bonusDamagePerTile (3)` bonus damage after displacement
 * resolves. Non-Astral displacements are ignored entirely — the listener
 * checks sourceTree and bails on mismatch.
 *
 * Event-driven: no tick cost. Registers once at construction time; the
 * displacement listener system has no removal API (same pattern as
 * AstralTeleportSystem), so destroy() is a no-op.
 *
 * Apotheosis parent: one of the two OR-gate parents for
 * `apotheosis_meteo` (Orbital Bombardment).
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § slingshot_passive
 * @see ADR-064 (attunement shared systems architecture)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Displacement event — fired after any knockback/pull resolves
// ---------------------------------------------------------------------------

export interface DisplacementEvent {
  /** ID of the enemy that was displaced. */
  readonly enemyId: string;
  /** Distance the enemy traveled, in tiles. May be 0 (wall-stopped). */
  readonly tilesTraveled: number;
  /** Attunement tree that sourced the displacement (e.g. 'astral', 'earth'). */
  readonly sourceTree: string;
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface OrbitalMechanicsDeps {
  /** Displacement event source — backed by KnockbackSystem / GravityPullSystem at runtime. */
  readonly displacement: {
    onDisplacementComplete(callback: (event: DisplacementEvent) => void): void;
  };
  /** Combat damage interface for applying bonus damage. */
  readonly combat: {
    dealBonusDamage(enemyId: string, amount: number, sourceSkillId: string): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface OrbitalMechanicsConfig {
  /** Bonus damage per tile of displacement (default 3). */
  readonly bonusDamagePerTile: number;
}

const DEFAULT_CONFIG: OrbitalMechanicsConfig = {
  bonusDamagePerTile: 3,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class OrbitalMechanicsPassiveSkillEffect implements SkillEffect {
  readonly skillId = 'slingshot_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: OrbitalMechanicsDeps;
  readonly config: OrbitalMechanicsConfig;

  /** Number of Astral displacements that triggered bonus damage. */
  private _displacementsProcessed = 0;
  /** Cumulative bonus damage dealt across all qualifying displacements. */
  private _totalBonusDamageDealt = 0;

  constructor(deps: OrbitalMechanicsDeps, config: Partial<OrbitalMechanicsConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };

    // Register at unlock time. No unregister API — listener persists
    // for the tree session lifetime (same pattern as AstralTeleportSystem).
    deps.displacement.onDisplacementComplete(this._onDisplacement.bind(this));
  }

  // --- State getters ---

  get displacementsProcessed(): number { return this._displacementsProcessed; }
  get totalBonusDamageDealt(): number { return this._totalBonusDamageDealt; }

  // ---------------------------------------------------------------------------
  // Displacement handler
  // ---------------------------------------------------------------------------

  private _onDisplacement(event: DisplacementEvent): void {
    // Only Astral-tree-sourced displacements qualify.
    if (event.sourceTree !== 'astral') return;

    this._displacementsProcessed++;

    const bonusDamage = event.tilesTraveled * this.config.bonusDamagePerTile;
    this._totalBonusDamageDealt += bonusDamage;

    // Zero-tile displacement (wall-stopped) yields 0 damage — skip the
    // dealBonusDamage call since there's nothing to apply.
    if (bonusDamage > 0) {
      this.deps.combat.dealBonusDamage(event.enemyId, bonusDamage, 'slingshot_passive');
    }
  }

  // ---------------------------------------------------------------------------
  // SkillEffect interface
  // ---------------------------------------------------------------------------

  activate(): boolean { return false; }
  isActive(): boolean { return false; }
  update(_delta: number): void { /* passive — event-driven, nothing to tick */ }
  destroy(): void { /* displacement listener system has no removal API */ }
}
